import { useState, useRef } from "react";
import { API_BASE } from "@/lib/api";

export type MediaProgressStep = "uploading" | "extracting" | "transcribing" | "finalizing";

export function useMediaSession(onSuccess: (sessionId: number) => void) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState<MediaProgressStep | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [limitType, setLimitType] = useState<string | undefined>(undefined);
  const [suggestedPlan, setSuggestedPlan] = useState<string | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);

  async function consumeSSE(response: Response) {
    if (!response.body) throw new Error("No response body");

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let done = false;

    while (!done) {
      const { value, done: readerDone } = await reader.read();
      done = readerDone;
      if (!value) continue;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split("\n").filter((l) => l.trim().startsWith("data: "));

      for (const line of lines) {
        const dataStr = line.replace(/^data: /, "").trim();
        if (!dataStr) continue;
        try {
          const data = JSON.parse(dataStr);

          if (data.error) {
            setError(data.error);
            if (data.limitType) setLimitType(data.limitType);
            if (data.suggestedPlan) setSuggestedPlan(data.suggestedPlan);
            done = true;
            break;
          }

          if (data.step) {
            setCurrentStep(data.step as MediaProgressStep);
          }

          if (data.sessionId) {
            done = true;
            onSuccess(data.sessionId);
            break;
          }
        } catch {
          // ignore unparseable lines
        }
      }
    }
  }

  async function submitYouTube(youtubeUrl: string, title: string, folderId: number | null) {
    setIsProcessing(true);
    setCurrentStep(null);
    setError(null);
    setLimitType(undefined);
    setSuggestedPlan(undefined);
    abortRef.current = new AbortController();

    const token = localStorage.getItem("studyflow_token");

    try {
      const response = await fetch(`${API_BASE}/api/sessions/youtube`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          youtubeUrl,
          title: title || undefined,
          ...(folderId != null ? { folderId } : {}),
        }),
        signal: abortRef.current.signal,
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        setError((body as any)?.message || "Failed to process YouTube URL");
        return;
      }

      await consumeSSE(response);
    } catch (err: any) {
      if (err.name !== "AbortError") {
        setError(err.message || "An error occurred");
      }
    } finally {
      setIsProcessing(false);
      abortRef.current = null;
    }
  }

  async function submitMedia(file: File, title: string, folderId: number | null) {
    setIsProcessing(true);
    setCurrentStep(null);
    setError(null);
    setLimitType(undefined);
    setSuggestedPlan(undefined);
    abortRef.current = new AbortController();

    const token = localStorage.getItem("studyflow_token");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", title);
      if (folderId != null) formData.append("folderId", String(folderId));

      const response = await fetch(`${API_BASE}/api/sessions/upload-media`, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
        signal: abortRef.current.signal,
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        setError((body as any)?.message || "Failed to process media file");
        return;
      }

      await consumeSSE(response);
    } catch (err: any) {
      if (err.name !== "AbortError") {
        setError(err.message || "An error occurred");
      }
    } finally {
      setIsProcessing(false);
      abortRef.current = null;
    }
  }

  return {
    isProcessing,
    currentStep,
    error,
    limitType,
    suggestedPlan,
    submitYouTube,
    submitMedia,
  };
}
